<template>
  <v-data-table-server
    fixed-header
    :class="[`ag-data-table elevation-${elevation}`, { 'ag-data-table--fixed-layout': fixedLayout }]"
    :headers="localHeaders"
    :height="computedHeight"
    :hide-default-footer="hideDefaultFooter"
    :items="items"
    :items-per-page="itemsPerPage"
    :no-data-text="noDataText"
    :page="page"
    :show-select="showSelect"
    :search="search"
    :items-length="serverItemsLength"
    :show-expand="showExpand"
    :single-expand="singleExpand"
    :items-per-page-text="itemsPerPageText"
    :items-per-page-options="itemsPerPageOptions"
    :sort-by="sortBy"
    @update:options="(val) => $emit('update:options', val)"
  >
    <template #top>
      <v-progress-linear v-show="loading" color="primary" height="4px" indeterminate rounded />
    </template>

    <template #headers="{ columns, isSorted, getSortIcon, toggleSort, sortBy, selectAll, allSelected, someSelected }">
      <tr>
        <th
          v-for="head in columns"
          :key="head.key"
          class="v-data-table__th"
          :class="headClass(head)"
          :data-is-sorted="getSortType(head, sortBy)"
          :data-sortable="head.sortable !== false"
          :data-qa="
            $attrs['data-qa'] ? `${$attrs['data-qa']}__coluna-${normalizeQa(head.value ?? head.key)}` : undefined
          "
          :style="{ width: head.width, minWidth: head.width }"
          @click="head.sortable !== false && head.key !== 'data-table-select' ? toggleSort(head) : undefined"
        >
          <v-checkbox-btn
            v-if="showSelect && head.key === 'data-table-select'"
            :model-value="allSelected"
            :indeterminate="someSelected && !allSelected"
            @update:model-value="selectAll(!allSelected)"
          />
          <div v-else class="ag-data-table__header-container">
            <div>
              <img v-if="head.isRecommendation" height="18" src="/favicon.ico" alt="logo" />
              <div style="line-height: 25px; padding-top: 1px">
                {{ head.text }}
              </div>
              <AGTooltip
                v-if="head.largeTooltip"
                top
                large-tooltip
                :large-tooltip-title="head.largeTooltipTitle"
                :large-tooltip-icon="head.largeTooltipIcon"
              >
                <template #largeTooltipContent>
                  <div v-html="head.largeTooltipContent" />
                </template>
                <AGIcon class="ag-data-table__help" :icon="head.helpIcon ?? 'md:help'" />
              </AGTooltip>
              <AGTooltip v-if="head.helpText" top :tooltip-text="head.helpText">
                <AGIcon class="ag-data-table__help" :icon="head.helpIcon ?? 'md:help'" />
              </AGTooltip>
            </div>
            <AGTooltip top :tooltip-text="getOrderTooltip(getSortIcon(head))">
              <v-icon v-if="head.sortable !== false">
                <template v-if="!isSorted(head)">mdi-swap-vertical</template>
                <template v-else>mdi-arrow-down</template>
              </v-icon>
            </AGTooltip>
          </div>
        </th>
      </tr>
    </template>

    <template v-if="loading" #body>
      <tr>
        <td v-for="head in headers" :key="head.value">
          <v-progress-linear color="#ECE7F5" height="25px" style="border-radius: 4px" indeterminate />
        </td>
      </tr>
    </template>

    <template v-for="row in rows" #[`item.${row.value}`]="props">
      <slot :name="`item.${row.value}`" v-bind="props" />
    </template>

    <template v-if="hasExpandSlot" #expanded-row="props">
      <slot name="expanded-item" v-bind="props" />
    </template>

    <template #no-data>
      <div
        class="ag-data-table__no-data"
        :data-qa="$attrs['data-qa'] ? `${$attrs['data-qa']}__estado-vazio` : undefined"
      >
        <slot v-if="hasNoDataSlot" name="no-data" />
        {{ noDataText }}
      </div>
    </template>

    <template #bottom="props">
      <slot name="bottom" v-bind="props">
        <v-data-table-footer
          v-if="!hideDefaultFooter"
          v-qa-paginacao="$attrs['data-qa']"
          v-bind="props"
          :items-per-page-text="itemsPerPageText"
          :items-per-page-options="itemsPerPageOptions"
        />
      </slot>
    </template>
  </v-data-table-server>
</template>

<script>
import AGIcon from '@/components/common/AGIcon.vue';
import AGTooltip from '@/components/common/AGTooltip.vue';
import UtilsString from '@/utils/UtilsString';

const QaPaginacaoDirective = {
  mounted(el, binding) {
    const dataQa = binding.value;
    if (!dataQa) return;

    const applyQa = () => {
      const target = el.classList.contains('v-data-table-footer') ? el : el.querySelector('.v-data-table-footer');
      if (!target) return;

      if (!target.hasAttribute('data-qa')) {
        target.setAttribute('data-qa', `${dataQa}__paginacao-container`);
      }

      const select = target.querySelector('.v-data-table-footer__items-per-page .v-input');
      if (select && !select.hasAttribute('data-qa')) {
        select.setAttribute('data-qa', `${dataQa}__paginacao-seletor`);
      }

      const info = target.querySelector('.v-data-table-footer__info');
      if (info && !info.hasAttribute('data-qa')) {
        info.setAttribute('data-qa', `${dataQa}__paginacao-info`);
      }

      const paginationBtns = target.querySelectorAll('.v-data-table-footer__pagination .v-btn');
      if (paginationBtns.length > 0) {
        const prev = paginationBtns[0];
        if (prev && !prev.hasAttribute('data-qa')) prev.setAttribute('data-qa', `${dataQa}__paginacao-anterior`);

        const next = paginationBtns[paginationBtns.length - 1];
        if (next && !next.hasAttribute('data-qa')) next.setAttribute('data-qa', `${dataQa}__paginacao-proxima`);
      }
    };

    applyQa();
    el._qaObserver = new MutationObserver(applyQa);
    el._qaObserver.observe(el, { childList: true, subtree: true });
  },
  unmounted(el) {
    if (el._qaObserver) el._qaObserver.disconnect();
  },
};

export default {
  components: { AGIcon, AGTooltip },
  directives: { QaPaginacao: QaPaginacaoDirective },
  emits: ['update:options', 'options'],
  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: undefined,
    },
    options: {
      type: Object,
      default: () => {},
    },
    page: {
      type: Number,
      default: 1,
    },
    serverItemsLength: {
      type: Number,
      default: -1,
    },
    footerProps: {
      type: Object,
      default: () => ({}),
    },
    noDataText: {
      type: String,
      default: 'Não há registros a serem exibidos',
    },
    itemsPerPage: {
      type: Number,
      default: 50,
    },
    itemsPerPageText: {
      type: String,
      default: 'Registros por página:',
    },
    elevation: {
      type: Number,
      default: 0,
    },
    showExpand: {
      type: Boolean,
      default: false,
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
    singleExpand: {
      type: Boolean,
      default: true,
    },
    height: {
      type: String,
      default: undefined,
    },
    hideDefaultFooter: {
      type: Boolean,
      default: false,
    },
    fixedLayout: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    localOptions: {
      get() {
        return this.options;
      },

      set(val) {
        this.$emit('update:options', val);
      },
    },

    localHeaders() {
      return this.headers.map((head) => {
        return {
          ...head,
          sortable: head.sortable !== false,
          cellProps: {
            ...head.cellProps,
            sticky: head.value === 'action',
          },
        };
      });
    },

    hasNoDataSlot() {
      return this.$slots['no-data'] ?? this.$slots['no-data'];
    },

    hasExpandSlot() {
      return this.$slots['expanded-item'] ?? this.$slots['expanded-item'];
    },

    rows() {
      return this.headers.filter((h) => {
        const key = `item.${h.value}`;
        return !!(this.$slots[key] ?? this.$slots[key]);
      });
    },

    itemsPerPageOptions() {
      return [
        { value: 50, title: '50' },
        { value: 100, title: '100' },
        { value: 250, title: '250' },
      ];
    },

    computedHeight() {
      return this.items.length < 10 && !this.loading ? undefined : this.height;
    },
  },
  methods: {
    normalizeQa: UtilsString.normalizeQa,
    headClass(head) {
      const textAlign = head.align ?? 'left';

      return {
        [`text-${textAlign}`]: true,
        'ag-data-table__sticky-cell': head.value === 'action',
      };
    },

    getOrderTooltip(iconType) {
      return !iconType ? 'Ordenar' : iconType === '$sortAsc' ? 'Crescente' : 'Decrescente';
    },

    getSortType(head, sortBy) {
      const obj = sortBy.find((sort) => sort.key === head.key);

      return obj?.order;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/table.scss';
</style>
